var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FormatAlignCenter, FormatAlignJustify, FormatAlignLeft, FormatAlignRight, FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered, FormatStrikethrough, FormatUnderlined, Redo, Undo, } from '@mui/icons-material';
import { Divider, MenuItem, Paper, Select, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectFontFamily } from './SelectFontFamily';
import { TextColorButton } from './TextColorButton';
import { Button } from '../Button';
import { isEmpty } from 'lodash';
var Heading;
(function (Heading) {
    Heading["HEADING1"] = "heading1";
    Heading["HEADING2"] = "heading2";
    Heading["HEADING3"] = "heading3";
})(Heading || (Heading = {}));
export var FontFamily;
(function (FontFamily) {
    FontFamily["ROBOTO"] = "Roboto";
    FontFamily["OPEN_SANS"] = "Open Sans";
    FontFamily["LATO"] = "Lato";
    FontFamily["MONTSERRAT"] = "Montserrat";
    FontFamily["POPPINS"] = "Poppins";
    FontFamily["RALEWAY"] = "Raleway";
    FontFamily["NOTO_SANS"] = "Noto Sans";
    FontFamily["INTER"] = "Inter";
    FontFamily["ROBOTO_SLAB"] = "Roboto Slab";
    FontFamily["MERRIWEATHER"] = "Merriweather";
})(FontFamily || (FontFamily = {}));
var textAlignFormats = ['left', 'right', 'center', 'justify'];
var textHeadingFormats = Object.values(Heading);
var fontFamilyFormats = Object.values(FontFamily);
var availableTextFormats = __spreadArray(__spreadArray(__spreadArray([
    'bold',
    'italic',
    'strike',
    'underline',
    'code',
    'paragraph',
    'bulletList',
    'orderedList',
    'codeBlock',
    'blockquote',
    'undo',
    'redo',
    'textColor'
], textAlignFormats, true), textHeadingFormats, true), fontFamilyFormats, true);
var getHeadingLevel = function (heading) { return Number(heading.charAt(heading.length - 1)); };
export var Toolbar = function (_a) {
    var editor = _a.editor, onSave = _a.onSave;
    if (!editor) {
        return null;
    }
    var t = useTranslation('core').t;
    var textColor = editor.getAttributes('textStyle').color || 'black';
    var activeFormats = availableTextFormats.filter(function (format) {
        if (textAlignFormats.includes(format)) {
            return editor.isActive({ textAlign: format });
        }
        else if (textHeadingFormats.includes(format)) {
            var level = getHeadingLevel(format);
            return editor.isActive('heading', { level: level });
        }
        else if (fontFamilyFormats.includes(format)) {
            return editor.isActive('textStyle', { fontFamily: format });
        }
        else {
            return editor.isActive(format);
        }
    });
    var alignment = useMemo(function () { return textAlignFormats.find(function (align) { return activeFormats.includes(align); }); }, [activeFormats]);
    var textSize = useMemo(function () { return __spreadArray(['paragraph'], textHeadingFormats, true).find(function (tsn) { return activeFormats.includes(tsn); }) || 'none'; }, [activeFormats]);
    var fontFamily = useMemo(function () { return fontFamilyFormats.find(function (fff) { return activeFormats.includes(fff); }) || ''; }, [activeFormats]);
    var handleTextColorChange = function (newColor) {
        return editor.chain().focus().setColor(String(newColor)).run();
    };
    var handleButtonClick = function (_ev, format) {
        handleTextFormat(format);
    };
    var handleTextFormat = function (format) {
        switch (format) {
            case 'left':
            case 'right':
            case 'center':
            case 'justify':
                return editor.chain().focus().setTextAlign(format).run();
            case 'bold':
                return editor.chain().focus().toggleBold().run();
            case 'italic':
                return editor.chain().focus().toggleItalic().run();
            case 'strike':
                return editor.chain().focus().toggleStrike().run();
            case 'underline':
                return editor.chain().focus().toggleUnderline().run();
            case 'paragraph':
                return editor
                    .chain()
                    .focus()
                    .setParagraph()
                    .setTextAlign(alignment || 'left')
                    .run();
            case Heading.HEADING1:
            case Heading.HEADING2:
            case Heading.HEADING3:
                var otherAttributes = editor.getAttributes('heading');
                if (isEmpty(otherAttributes)) {
                    otherAttributes = editor.getAttributes('paragraph');
                }
                return editor
                    .chain()
                    .focus()
                    .setHeading(__assign(__assign({}, otherAttributes), { level: getHeadingLevel(format) }))
                    .run();
            case FontFamily.INTER:
            case FontFamily.MONTSERRAT:
            case FontFamily.MERRIWEATHER:
            case FontFamily.LATO:
            case FontFamily.NOTO_SANS:
            case FontFamily.OPEN_SANS:
            case FontFamily.ROBOTO_SLAB:
            case FontFamily.ROBOTO:
            case FontFamily.POPPINS:
            case FontFamily.RALEWAY:
                return editor.chain().focus().setFontFamily(format).run();
            case 'bulletList':
                return editor.chain().focus().toggleBulletList().run();
            case 'orderedList':
                return editor.chain().focus().toggleOrderedList().run();
            case 'undo':
                return editor.chain().focus().undo().run();
            case 'redo':
                return editor.chain().focus().redo().run();
        }
    };
    var onSelectChange = function (ev) {
        handleTextFormat(ev.target.value);
    };
    return (_jsxs(Paper, __assign({ elevation: 0, className: 'toolbar' }, { children: [_jsx(SelectFontFamily, { value: fontFamily, onChange: onSelectChange }), _jsxs(Select, __assign({ value: textSize, onChange: onSelectChange }, { children: [_jsx(MenuItem, __assign({ value: 'heading1' }, { children: t('richTextEditor.toolbar.textSizeLabels.heading1') })), _jsx(MenuItem, __assign({ value: 'heading2' }, { children: t('richTextEditor.toolbar.textSizeLabels.heading2') })), _jsx(MenuItem, __assign({ value: 'heading3' }, { children: t('richTextEditor.toolbar.textSizeLabels.heading3') })), _jsx(MenuItem, __assign({ value: 'paragraph' }, { children: t('richTextEditor.toolbar.textSizeLabels.paragraph') })), _jsx(MenuItem, { value: 'none', sx: { display: 'none' } })] })), _jsx(Divider, { orientation: 'vertical', flexItem: true }), _jsxs(ToggleButtonGroup, __assign({ className: 'button-group', exclusive: true, value: alignment }, { children: [_jsx(ToggleButton, __assign({ value: "left", onClick: handleButtonClick }, { children: _jsx(FormatAlignLeft, {}) })), _jsx(ToggleButton, __assign({ value: "center", onClick: handleButtonClick }, { children: _jsx(FormatAlignCenter, {}) })), _jsx(ToggleButton, __assign({ value: "right", onClick: handleButtonClick }, { children: _jsx(FormatAlignRight, {}) })), _jsx(ToggleButton, __assign({ value: "justify", onClick: handleButtonClick }, { children: _jsx(FormatAlignJustify, {}) }))] })), _jsx(Divider, { orientation: 'vertical', flexItem: true }), _jsx(TextColorButton, { onChange: handleTextColorChange, value: textColor }), _jsxs(ToggleButtonGroup, __assign({ value: activeFormats }, { children: [_jsx(ToggleButton, __assign({ value: "bold", onClick: handleButtonClick }, { children: _jsx(FormatBold, {}) })), _jsx(ToggleButton, __assign({ value: "italic", onClick: handleButtonClick }, { children: _jsx(FormatItalic, {}) })), _jsx(ToggleButton, __assign({ value: "strike", onClick: handleButtonClick }, { children: _jsx(FormatStrikethrough, {}) })), _jsx(ToggleButton, __assign({ value: "underline", onClick: handleButtonClick }, { children: _jsx(FormatUnderlined, {}) }))] })), _jsx(Divider, { orientation: 'vertical', flexItem: true }), _jsxs(ToggleButtonGroup, __assign({ value: activeFormats }, { children: [_jsx(ToggleButton, __assign({ value: "bulletList", onClick: handleButtonClick }, { children: _jsx(FormatListBulleted, {}) })), _jsx(ToggleButton, __assign({ value: "orderedList", onClick: handleButtonClick }, { children: _jsx(FormatListNumbered, {}) }))] })), _jsx(Divider, { orientation: 'vertical', flexItem: true }), _jsxs(ToggleButtonGroup, { children: [_jsx(ToggleButton, __assign({ value: "undo", onClick: handleButtonClick, disabled: !editor.can().chain().focus().undo().run() }, { children: _jsx(Undo, {}) })), _jsx(ToggleButton, __assign({ value: "redo", onClick: handleButtonClick, disabled: !editor.can().chain().focus().redo().run() }, { children: _jsx(Redo, {}) }))] }), onSave && (_jsxs(_Fragment, { children: [_jsx(Divider, { orientation: 'vertical', flexItem: true }), _jsx("div", { children: _jsx(Button, { label: t('richTextEditor.toolbar.saveButton'), onClick: onSave }) })] }))] })));
};
export default Toolbar;
